'use client';

import { useState } from 'react';
import { BookOpen, ClipboardCopy, Code2, ExternalLink, MessageSquareText, MousePointerClick } from 'lucide-react';
import AIScansShell from '@/components/ai-scans/AIScansShell';
import CopyButton from '@/components/ai-scans/CopyButton';
import { buildClaudePrompt } from '@/components/ai-scans/claudePrompt';
import { PUBLIC_SCREENER_API_URL } from '@/lib/api/screenerApi';

const EXAMPLE_QUESTIONS = [
  'Find profitable small caps with ROE above 20% and debt to equity below 0.5',
  'Which banks have the best 1-year returns?',
  "Summarise today's board meeting announcements",
  'Compare TCS and INFY on growth and valuation',
  'Which stocks are near their 52-week high with strong 3-month momentum?',
  'Show companies where promoters increased their holding over the last year',
];

const ENDPOINTS = [
  { path: '/guide', purpose: 'How to use the API, written for AI assistants (Markdown). Read this first.' },
  { path: '/screener/meta', purpose: 'Views, every column with its unit and meaning, industries, last update time.' },
  { path: '/screener/stocks', purpose: 'Filter and sort all stocks: view, q, industry, exchange, sort, order, <column>_min / _max, limit, offset.' },
  { path: '/screener/stocks/{exchange}/{symbol}', purpose: 'One stock with every metric and its 20 latest announcements.' },
  { path: '/screener/announcements', purpose: 'Company filings, newest first: q, exchange + symbol, from / to dates, limit, offset.' },
];

const GUIDE_URL = `${PUBLIC_SCREENER_API_URL}/guide`;
const CURL_EXAMPLE = `curl "${PUBLIC_SCREENER_API_URL}/screener/stocks?view=valuation&roe_pct_min=20&debt_to_equity_max=0.5&market_cap_cr_min=500&sort=roe_pct&limit=10"`;

const STEPS = [
  {
    icon: ClipboardCopy,
    title: 'Copy the prompt',
    text: 'It tells Claude where our free data lives and how to answer you.',
  },
  {
    icon: MousePointerClick,
    title: 'Open Claude and paste it',
    text: 'Use claude.ai, the Claude desktop app or Claude Code — any Claude that can browse the web.',
  },
  {
    icon: MessageSquareText,
    title: 'Ask your questions',
    text: 'Ask follow-ups in plain English. Claude fetches fresh numbers every time.',
  },
];

export default function UseWithClaudePage() {
  const [question, setQuestion] = useState('');
  const prompt = buildClaudePrompt(question);

  return (
    <AIScansShell>
      <header className="max-w-3xl">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl dark:text-white">Use with Claude</h1>
        <p className="mt-2 text-sm text-zinc-500 md:text-base dark:text-zinc-400">
          Claude can read our stock data for you. Ask things like &ldquo;which banks grew profits fastest?&rdquo; and
          get an answer with real numbers — no sign-up, no API key, no coding.
        </p>
      </header>

      <ol className="mt-6 grid gap-3 md:grid-cols-3">
        {STEPS.map(({ icon: Icon, title, text }, index) => (
          <li key={title} className="flex gap-3 rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">
              {index + 1}
            </span>
            <div>
              <p className="flex items-center gap-1.5 font-semibold text-zinc-900 dark:text-white">
                <Icon className="h-4 w-4 text-emerald-500" />
                {title}
              </p>
              <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">{text}</p>
            </div>
          </li>
        ))}
      </ol>

      <section className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 sm:p-6 dark:border-zinc-800 dark:bg-zinc-900">
        <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Pick a first question (optional)</h2>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Tap an example or type your own. It goes on the last line of the prompt.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {EXAMPLE_QUESTIONS.map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => setQuestion(example)}
              aria-pressed={question === example}
              className={`rounded-full border px-3 py-1.5 text-left text-sm transition-colors ${
                question === example
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
                  : 'border-zinc-200 text-zinc-700 hover:border-emerald-500/50 dark:border-zinc-700 dark:text-zinc-300'
              }`}
            >
              {example}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="Or type your own question…"
          aria-label="Your first question"
          className="mt-3 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
        />

        <div className="mt-5 overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center justify-between gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-2 dark:border-zinc-700 dark:bg-zinc-800/60">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Your prompt</span>
            <CopyButton text={prompt} variant="icon" label="Copy prompt" />
          </div>
          <pre className="max-h-56 overflow-auto sm:max-h-80 whitespace-pre-wrap break-words bg-white p-4 font-mono text-xs leading-relaxed text-zinc-700 sm:text-[13px] dark:bg-zinc-950 dark:text-zinc-300">
            {prompt}
          </pre>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <CopyButton text={prompt} label="Copy prompt" copiedLabel="Copied! Now paste it in Claude" variant="primary" />
          <a
            href="https://claude.ai/new"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 px-5 py-3 text-sm font-semibold text-zinc-800 transition-colors hover:border-emerald-500/50 dark:border-zinc-700 dark:text-zinc-100"
          >
            Open Claude
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
        <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
          Claude&apos;s answers are research help, not investment advice. Double-check important numbers before you invest.
        </p>
      </section>

      <section className="mt-10">
        <h2 className="flex items-center gap-2 text-lg font-bold tracking-tight text-zinc-900 dark:text-white">
          <Code2 className="h-5 w-5 text-emerald-500" />
          For developers &amp; MCP
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-zinc-500 dark:text-zinc-400">
          The same data is an open, read-only JSON API. No key, CORS open, GET only. Building an agent or using an MCP
          client with a web-fetch tool? Point it at the guide URL and it will work out the rest.
        </p>

        <dl className="mt-4 grid gap-3 md:grid-cols-2">
          {[
            { label: 'Base URL', value: PUBLIC_SCREENER_API_URL },
            { label: 'Guide for AI assistants', value: GUIDE_URL },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-xl border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
              <dt className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{label}</dt>
              <dd className="mt-1 flex items-center justify-between gap-2">
                <code className="min-w-0 break-all font-mono text-sm text-zinc-900 dark:text-zinc-100">{value}</code>
                <CopyButton text={value} variant="icon" label={`Copy ${label}`} />
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-4 overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
          <p className="border-b border-zinc-100 bg-zinc-50 px-4 py-2.5 text-xs font-semibold text-zinc-500 dark:border-zinc-800 dark:bg-zinc-800/60 dark:text-zinc-400">
            GET endpoints
          </p>
          <dl className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {ENDPOINTS.map(({ path, purpose }) => (
              <div key={path} className="grid gap-1 px-4 py-2.5 text-sm md:grid-cols-[minmax(0,20rem)_1fr] md:gap-4">
                <dt className="break-all font-mono text-xs text-emerald-700 md:pt-0.5 dark:text-emerald-400">{path}</dt>
                <dd className="text-zinc-600 dark:text-zinc-400">{purpose}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center justify-between gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-2 dark:border-zinc-800 dark:bg-zinc-800/60">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Example</span>
            <CopyButton text={CURL_EXAMPLE} variant="icon" label="Copy curl example" />
          </div>
          <pre className="whitespace-pre-wrap break-all bg-zinc-950 p-4 font-mono text-xs leading-relaxed text-zinc-100">{CURL_EXAMPLE}</pre>
        </div>

        <ul className="mt-4 space-y-1.5 text-sm text-zinc-600 dark:text-zinc-400">
          <li>
            <strong className="font-semibold text-zinc-800 dark:text-zinc-200">Rate limit:</strong> 60 requests a minute per IP
            address. Going over returns <code className="font-mono text-xs">429</code> with a{' '}
            <code className="font-mono text-xs">Retry-After</code> header.
          </li>
          <li>
            <strong className="font-semibold text-zinc-800 dark:text-zinc-200">Freshness:</strong> prices hourly in market
            hours, financials daily. Screener responses include a <code className="font-mono text-xs">last_synced</code> time.
          </li>
          <li>
            <a
              href={GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-semibold text-emerald-600 hover:underline dark:text-emerald-400"
            >
              <BookOpen className="h-4 w-4" />
              Read the full guide
            </a>
          </li>
        </ul>
      </section>
    </AIScansShell>
  );
}
